import { CalendarBlank, Clock, Flag, Repeat, Trash } from '@phosphor-icons/react';
import { shiftOn, WEEKDAY_SHORT, type ShiftPlan } from '@shh/shared';
import {
  Avatar,
  Button,
  ChoiceGroup,
  cn,
  FormSection,
  MonthPicker,
  personSolid,
  Text,
  TextField,
} from '@shh/ui';
import { useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { deleteShiftPlan, saveShiftPlan } from '../data/api';
import { useFamily, type WithId } from '../data/family';
import { formsText } from '../i18n/forms';
import { locale } from '../lib/locale';
import { dayTitle, dayTitleShort, monthName, monthTitle } from './calendar/format';
import { Actions, useSave } from './forms';

/*
 * A shift plan (1.25, Mike 19.10.: "2x früh, 2x spät, 2x Nacht und dann 3 Tage frei, das als
 * Serie"): named shifts with times, a rhythm of days that repeats, the first day. No event – its
 * own entry, like in the shift apps; single days are swapped right in the calendar.
 */

type Key = 'F' | 'S' | 'N' | 'T';
type Day = Key | '-';
const KEYS: Key[] = ['F', 'S', 'N', 'T'];
const DEFAULT_TIMES: Record<Key, [string, string]> = {
  F: ['06:00', '14:00'],
  S: ['14:00', '22:00'],
  N: ['22:00', '06:00'],
  T: ['07:00', '19:00'],
};
const PRESETS: { id: string; label: string; days: Day[] }[] = [
  {
    id: 'rotating',
    label: formsText.presetRotating,
    days: ['F', 'F', 'S', 'S', 'N', 'N', '-', '-', '-'],
  },
  {
    id: 'weekly',
    label: formsText.presetWeekly,
    days: ['F', 'F', 'F', 'F', 'F', '-', '-', 'S', 'S', 'S', 'S', 'S', '-', '-'],
  },
  {
    id: 'fourFour',
    label: formsText.presetFourFour,
    days: ['T', 'T', 'T', 'T', '-', '-', '-', '-'],
  },
];
// a shift and its day in the rhythm look the same: solid in the person's line, free stays open
// each shift its own colour, like in the prototype (Mike, 21.10.) – the person palette is
// contrast-tested in light, dark and the wall at night
const SHIFT_COLOR: Record<Key, string> = {
  F: personSolid.sun,
  S: personSolid.clay,
  N: personSolid.plum,
  T: personSolid.meadow,
};
const tile = (k: Day) =>
  k === '-' ? 'border-2 border-dashed border-border-default text-text-muted' : SHIFT_COLOR[k];

export function ShiftForm({
  plan,
  memberId,
  fixedMemberId,
  onDone,
}: {
  plan?: WithId<ShiftPlan>;
  memberId?: string;
  /** Picked outside (1.28: "Schicht- oder Stundenplan" keeps the person when switching). */
  fixedMemberId?: string;
  onDone: () => void;
}) {
  const f = useFamily();
  const [picked, setWho] = useState(plan?.memberId ?? memberId ?? f.meId ?? f.members[0]?.id ?? '');
  const who = fixedMemberId ?? picked;
  const [types, setTypes] = useState<Record<Key, { name: string; start: string; end: string }>>(
    () =>
      Object.fromEntries(
        KEYS.map((k) => [
          k,
          plan?.types[k] ?? {
            name: formsText.shiftNames[k],
            start: DEFAULT_TIMES[k][0],
            end: DEFAULT_TIMES[k][1],
          },
        ]),
      ) as Record<Key, { name: string; start: string; end: string }>,
  );
  const [pattern, setPattern] = useState<Day[]>(
    (plan?.pattern as Day[] | undefined) ?? PRESETS[0]!.days,
  );
  const [start, setStart] = useState(plan?.start ?? f.today);
  const [until, setUntil] = useState(plan?.until ?? '');
  const { busy, error, save } = useSave(onDone);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const used = KEYS.filter((k) => pattern.includes(k));
  // Tag (T) only shows once it is used – most plans are early / late / night
  const offered = KEYS.filter((k) => k !== 'T' || used.includes('T'));
  const preset = PRESETS.find((p) => p.days.join() === pattern.join())?.id ?? 'custom';
  const letter = (k: Day) => (k === '-' ? '–' : (types[k].name[0] ?? k));
  // a new plan starts with the template, editing shows all lines folded
  const [open, setOpen] = useState<'rhythm' | 'day' | 'types' | 'until' | null>(
    plan ? null : 'rhythm',
  );
  const toggle = (k: 'rhythm' | 'day' | 'types' | 'until') => (o: boolean) => setOpen(o ? k : null);

  return (
    <form className="flex flex-col gap-gap-md" onSubmit={(e) => e.preventDefault()}>
      {!fixedMemberId && (
        <ChoiceGroup
          label={formsText.shiftFor}
          layout="person"
          value={who}
          onChange={setWho}
          choices={f.members.map((m) => ({
            value: m.id,
            label: m.name,
            picture: <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />,
          }))}
        />
      )}

      {/* the same lines as the event form (1.25): a template is the first tap */}
      <div className="flex flex-col">
        <FormSection
          icon={Clock}
          label={formsText.shifts}
          summary={used
            .map((k) => `${types[k].name} ${types[k].start}–${types[k].end}`)
            .join(' · ')}
          isOpen={open === 'types'}
          onOpenChange={toggle('types')}
        >
          <Text size="sm" tone="secondary">
            {formsText.shiftsHint}
          </Text>
          {offered.map((k) => (
            <div key={k} className="grid grid-cols-2 gap-gap-sm sm:grid-cols-3">
              <TextField
                label={formsText.shiftName}
                value={types[k].name}
                onChange={(v) => setTypes({ ...types, [k]: { ...types[k], name: v.slice(0, 20) } })}
                className="col-span-2 sm:col-span-1"
              />
              <TextField
                label={formsText.from}
                type="time"
                value={types[k].start}
                onChange={(v) => v && setTypes({ ...types, [k]: { ...types[k], start: v } })}
              />
              <TextField
                label={formsText.to}
                type="time"
                value={types[k].end}
                onChange={(v) => v && setTypes({ ...types, [k]: { ...types[k], end: v } })}
              />
            </div>
          ))}
        </FormSection>

        <FormSection
          icon={Repeat}
          label={formsText.rhythm}
          summary={PRESETS.find((p) => p.id === preset)?.label ?? pattern.map(letter).join(' ')}
          isOpen={open === 'rhythm'}
          onOpenChange={toggle('rhythm')}
        >
          <ChoiceGroup
            label={formsText.rhythm}
            value={preset}
            onChange={(v) => {
              const p = PRESETS.find((x) => x.id === v);
              if (p) setPattern(p.days);
              setOpen('day');
            }}
            choices={PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          />
          <Text size="sm" tone="secondary">
            {formsText.rhythmHint}
          </Text>
          <div className="flex flex-wrap gap-gap-xs">
            {[...offered, '-' as const].map((k) => (
              <AriaButton
                key={k}
                onPress={() => pattern.length < 28 && setPattern([...pattern, k])}
                className={cn(
                  'min-h-touch-min cursor-pointer rounded-control px-inset-sm font-bold outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                  tile(k),
                )}
              >
                {formsText.addShift(k === '-' ? formsText.off : types[k].name)}
              </AriaButton>
            ))}
          </div>
          <ol
            aria-label={formsText.rhythm}
            className="flex flex-wrap gap-gap-xs rounded-card bg-surface-raised p-inset-xs"
          >
            {pattern.map((k, i) => (
              <li key={i}>
                <AriaButton
                  aria-label={formsText.removeDay(
                    String(i + 1),
                    k === '-' ? formsText.off : types[k].name,
                  )}
                  onPress={() =>
                    pattern.length > 1 && setPattern(pattern.filter((_, j) => j !== i))
                  }
                  className={cn(
                    'grid size-touch-min cursor-pointer place-items-center rounded-control font-black outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                    tile(k),
                  )}
                >
                  {letter(k)}
                </AriaButton>
              </li>
            ))}
          </ol>
          <Text size="sm" tone="muted">
            {formsText.rhythmLength(String(pattern.length))}
          </Text>
        </FormSection>

        <FormSection
          icon={CalendarBlank}
          label={formsText.dayOne}
          summary={dayTitleShort(start)}
          isOpen={open === 'day'}
          onOpenChange={toggle('day')}
        >
          {/* the month shows the shifts under the days – it is the preview */}
          <MonthPicker
            value={start}
            today={f.today}
            weekdays={[...WEEKDAY_SHORT[locale]]}
            monthLabel={monthTitle}
            monthName={monthName}
            dayLabel={dayTitle}
            prevLabel={formsText.prevMonth}
            nextLabel={formsText.nextMonth}
            pickMonthLabel={formsText.pickMonth}
            onPick={(d) => {
              setStart(d);
              setOpen(null);
            }}
            mark={(d) => {
              const k = shiftOn({ start, pattern, until: until || undefined }, d) as Day | null;
              return k && k !== '-' ? (
                <span
                  aria-hidden
                  className={cn(
                    'grid size-4 place-items-center rounded-sm text-xs font-black',
                    tile(k),
                  )}
                >
                  {letter(k)}
                </span>
              ) : null;
            }}
          />
        </FormSection>

        <FormSection
          icon={Flag}
          label={formsText.planEnds}
          summary={until ? dayTitleShort(until) : undefined}
          placeholder={formsText.optional}
          isOpen={open === 'until'}
          onOpenChange={toggle('until')}
        >
          <TextField label={formsText.planUntil} type="date" value={until} onChange={setUntil} />
        </FormSection>
      </div>

      {plan && (
        // deleting is two taps – a whole rhythm is a lot to set up again
        <Button
          variant={confirmDelete ? 'danger' : 'quiet'}
          icon={Trash}
          className="self-start"
          onPress={() =>
            confirmDelete
              ? void deleteShiftPlan(f.householdId, plan.id).then(onDone)
              : setConfirmDelete(true)
          }
        >
          {confirmDelete ? formsText.deletePlanSure : formsText.deletePlan}
        </Button>
      )}

      <Actions
        onCancel={onDone}
        saveLabel={plan ? formsText.save : formsText.addShiftPlan}
        disabled={!who || !pattern.some((k) => k !== '-')}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveShiftPlan(f.householdId, {
              id: plan?.id,
              memberId: who,
              types: Object.fromEntries(used.map((k) => [k, types[k]])),
              pattern,
              start,
              ...(until && until >= start ? { until } : {}),
              ...(plan?.over ? { over: plan.over } : {}),
              createdBy: plan?.createdBy ?? f.meId,
            }),
          )
        }
      />
    </form>
  );
}
